import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CategoryIcon from "@mui/icons-material/Category";
import { Grid, MenuItem, Stack, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { APP_ICONS } from "../../../assets";
import AppLoader from "../../../components/AppLoader";
import PaperContainer from "../../../components/Containers/PaperContainer";
import CustomSelect from "../../../components/customSelect";
import GlobalTable from "../../../components/Tables/GlobalTable";
import TextInput from "../../../components/TextInputs";
import useDebounce from "../../../hooks/useDebounce";
import { setSearchKeys } from "../../../lib/slices/searchKeysSlice";
import {
  exportProductReviews,
  getProductReviews,
} from "../../../services/productApiFn";
import { getAllSupplier } from "../../../services/supplierApiFn";
import { handleOpenSnackBar } from "../../../utils";
import { CSVLink } from "react-csv";
import GlobalButton from "../../../components/CustomButton";
import moment from "moment";

const headerData = ["reviewedAt","store", "productDetails", "userDetails"];

const tableRows = [
  "created_date",
  "restore_store",
  "reviewed_product_detail",
  "user_details_review",
];

function AllProductsReviews() {
  const dispatch = useDispatch();
  const csvLinkRef = useRef(null);
  const { t } = useTranslation(["products", "common"]);
  const [isLoading, setIsLoading] = useState([]);
  const [allReviews, setAllReviews] = useState([]);
  const [allSuppliers, setAllSuppliers] = useState([]);
  const [exportData, setExportData] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const userData = useSelector((state) => state.userData.user);
  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
  const debouncedSearchTerm = useDebounce(searchKeys.productNameReview, 1000);

  const fetchAllSuppliers = async () => {
    try {
      const response = await getAllSupplier("");
      if (response.data.status === "success") {
        setAllSuppliers(response.data.data.suppliers);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    }
  };

  useEffect(() => {
    fetchAllSuppliers();
  }, []);

  useEffect(() => {
    if (userData) {
      fetchAllReviews(debouncedSearchTerm, searchKeys.vendorIdForReview);
    }
  }, [userData, debouncedSearchTerm, searchKeys.vendorIdForReview]);

  const fetchAllReviews = async (productName, vendorId) => {
    let query = {
      search: productName || "",
      page: 1,
      limit: 50,
      supplierId: vendorId === "all" ? "" : vendorId,
    };

    try {
      setIsLoading(true);
      const response = await getProductReviews(query);
      if (response?.data.status === "success") {
        let data = response?.data?.data.data;
        // handleOpenSnackBar("success", response.data.message);
        setAllReviews(data);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const renderSearchAction = () => {
    return (
      <Stack p={2} bgcolor={"primary.light"}>
        <Grid container spacing={2} alignItems={"center"}>
          <Grid item xs={12} md={6} lg={4}>
            <TextInput
              InputStartIcon={<img src={APP_ICONS.searchGreyIcon} width={25} />}
              InputEndIcon={
                searchKeys.productNameReview && (
                  <CancelOutlinedIcon
                    fontSize="small"
                    sx={{ cursor: "pointer" }}
                    onClick={() => {
                      dispatch(
                        setSearchKeys({
                          ...searchKeys,
                          productNameReview: "",
                        }),
                      );
                    }}
                  />
                )
              }
              placeholder={t("searchByName", { ns: "common" })}
              value={searchKeys.productNameReview}
              onChange={(e) => {
                dispatch(
                  setSearchKeys({
                    ...searchKeys,
                    productNameReview: e.target.value,
                  }),
                );
              }}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <CustomSelect
              placeholder={t("suppliers", { ns: "common" })}
              formStyleProps={{
                "& .MuiInputBase-input": {
                  textTransform: "capitalize",
                },
              }}
              icon={<CategoryIcon sx={{ fontSize: "15px", color: "#000000" }} />}
              value={searchKeys.vendorIdForReview || "all"}
              onChange={(e) => {
                dispatch(
                  setSearchKeys({
                    ...searchKeys,
                    vendorIdForReview: e.target.value,
                  }),
                );
              }}
            >
              <MenuItem value="all">{t("all", { ns: "common" })}</MenuItem>
              {allSuppliers.map((val, ind) => (
                <MenuItem
                  key={ind}
                  value={val._id}
                  sx={{ textTransform: "capitalize" }}
                >
                  {val.businessName}
                </MenuItem>
              ))}
            </CustomSelect>
          </Grid>
        </Grid>
      </Stack>
    );
  };

  const prepareExportData = async () => {
    try {
      setExportLoading(true);
      const query = {
        supplierId:
          searchKeys.vendorIdForReview === "all"
            ? ""
            : searchKeys.vendorIdForReview,
      };
      const response = await exportProductReviews(query);
      if (response?.data?.status === "success") {
        const data = response?.data?.data;
        const formattedData = [];
        data.forEach((item) => {
          formattedData.push({
            "Created At": moment(item.createdAt).format("MM-DD-YYYY") || "-",
            "Product Id": item.productId || "-",
            "Cover Image": item.productImage || "-",
            "Name In (Ro)": item.productName?.ro || "-",
            "Name In (En)": item.productName?.en || "-",
            User: item?.username,
            Comment: item.comment,
            Rating: item.rating?.toFixed(0) || "-",
          });
        });

        setExportData(formattedData);

        // Data set hone ke baad manually click trigger karo
        setTimeout(() => {
          csvLinkRef.current?.link?.click();
        }, 100);
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setExportLoading(false);
    }
  };

  const renderHeaderAction = () => {
    return (
      <Stack
        gap={1}
        flexGrow={1}
        direction={{ sm: "row" }}
        alignItems={"center"}
        justifyContent={"flex-end"}
      >
        <CSVLink
          ref={csvLinkRef}
          data={exportData}
          filename={"all_reviews.csv"}
          className="csv-link"
          target="_blank"
          style={{ display: "none" }}
        />

        <GlobalButton
          variant={"export_button"}
          btnText={"export"}
          startIcon={<img src={APP_ICONS.exportBlockIcon} width={20} />}
          disabled={exportLoading}
          handleClickBtn={prepareExportData}
        />
      </Stack>
    );
  };

  return (
    <>
      <AppLoader isVisible={isLoading} />
      <PaperContainer
        title={t("allReviews")}
        subtitle={t("viewClientReviews")}
        searchActions={renderSearchAction()}
        headerActions={renderHeaderAction()}
      >
        <GlobalTable
          tableHeader={headerData}
          displayRows={tableRows}
          tableData={allReviews}
        />
        {allReviews?.length === 0 && isLoading === false && (
          <Typography variant="h5" fontWeight={600} textAlign={"center"}>
            {t("noDataFound", { ns: "common" })}!
          </Typography>
        )}
      </PaperContainer>
    </>
  );
}

export default AllProductsReviews;
